import React, { FC, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { useFreeBuildingsSummary } from '~/hooks/freeBuildings/useFreeBuildingsSummary';
import { convertTableColumnsToCsvHeaders } from '~/helpers/helpers';
import { FreeBuildingSummaryAccessor } from '~/types/freeBuildingsSummary';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import Spinner from '~/components/Spinner/Spinner';
import classes from './FreeBuildingsSummaryTable.module.css';

const formatPercent = (value: number | null) =>
  value === null ? '--' : `${value}%`;

const StatTile: FC<{ title: string; description: string; value: string }> = ({
  title,
  description,
  value
}) => (
  <div className={classes.statTile}>
    <div>
      <div className={classes.statTitle}>{title}</div>
      <div className={classes.statDescription}>{description}</div>
    </div>
    <div className={classes.statValue}>{value}</div>
  </div>
);

export const FreeBuildingsSummaryTable: FC = () => {
  const {
    loading,
    buildings,
    tableColumns,
    summaryStats,
    eventsFilter,
    setEventsFilter,
    commentsFilter,
    setCommentsFilter,
    searchValue,
    setSearchValue
  } = useFreeBuildingsSummary();

  const csvData = useMemo(
    () =>
      buildings.map((building) => ({
        ...building,
        [FreeBuildingSummaryAccessor.adoptionRate]: formatPercent(
          building[FreeBuildingSummaryAccessor.adoptionRate]
        ),
        [FreeBuildingSummaryAccessor.activeRate]: formatPercent(
          building[FreeBuildingSummaryAccessor.activeRate]
        )
      })),
    [buildings]
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div>
      <div className={classes.header}>
        <div className='title bold tableTitle'>Building Engagement Summary</div>
        <p className={classes.subtitle}>
          Resident engagement across your assigned Free buildings, updated
          daily. Spot adoption gaps and inactive communities at a glance.
        </p>
      </div>

      <div className={classes.summaryRow}>
        <StatTile
          title='Buildings'
          description='Matching current filters'
          value={String(summaryStats.buildingCount)}
        />
        <StatTile
          title='Avg. adoption rate'
          description='Total users ÷ occupied units'
          value={formatPercent(summaryStats.avgAdoptionRate)}
        />
        <StatTile
          title='Avg. active rate'
          description='MAU ÷ total users'
          value={formatPercent(summaryStats.avgActiveRate)}
        />
        <StatTile
          title='Events (90d)'
          description='Total across matching buildings'
          value={String(summaryStats.totalEvents)}
        />
        <StatTile
          title='RSVPs (30d)'
          description='Total across matching buildings'
          value={String(summaryStats.totalRSVPs)}
        />
      </div>

      <div className={classes.filterPanel}>
        <input
          type='text'
          placeholder='Search by building name'
          className={classes.searchInput}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <div className={classes.filterRow}>
          <span className={classes.filterLabel}>Events (past 90d)</span>
          <select
            className={classes.filterSelect}
            value={eventsFilter}
            onChange={(e) => setEventsFilter(e.target.value as typeof eventsFilter)}
          >
            <option value='all'>All</option>
            <option value='has'>Has events</option>
            <option value='no'>No events</option>
          </select>
        </div>
        <div className={classes.filterRow}>
          <span className={classes.filterLabel}>Comments (past 30d)</span>
          <select
            className={classes.filterSelect}
            value={commentsFilter}
            onChange={(e) =>
              setCommentsFilter(e.target.value as typeof commentsFilter)
            }
          >
            <option value='all'>All</option>
            <option value='has'>Has comments</option>
            <option value='no'>No comments</option>
          </select>
        </div>
        <CSVLink
          data={csvData}
          filename='free-buildings-engagement-summary.csv'
          headers={convertTableColumnsToCsvHeaders(tableColumns)}
          className={`btn-standard btn-primary ${classes.csvButton}`}
        >
          Download CSV
        </CSVLink>
      </div>

      <CobuTableAdvanced
        columns={tableColumns}
        data={buildings}
        noData={{ label: 'No buildings match your filters' }}
        sort={{ id: FreeBuildingSummaryAccessor.buildingName, descending: false }}
        showPagination
      />
    </div>
  );
};
